import { useState } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import clsx from 'clsx'
import { IconLeft, IconRight } from 'react-day-picker'

import { EmptyChartData } from '@/components/charts/empty-chart-data.tsx'
import { CreatorBrowserLink } from '@/components/ui/CreatorBrowserLink.tsx'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import type { CreatorFanStats } from '@/modules/overview/types'

interface Props {
  title?: string
  topFanList?: CreatorFanStats[] | undefined
  isFetching: boolean
  fullHeight?: boolean
  showCents?: boolean
  enablePagination?: boolean
  itemsPerPage?: number
}

export const TopFansStats = ({
  title = 'Top Fans',
  topFanList,
  isFetching,
  fullHeight,
  showCents,
  enablePagination,
  itemsPerPage = 5,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(1) // State for the current page

  // Calculate the items to display based on pagination
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedTopFanList = enablePagination
    ? topFanList?.slice(startIndex, endIndex)
    : topFanList
  const totalPages = topFanList
    ? Math.ceil(topFanList.length / itemsPerPage)
    : 0

  function handlePrevious() {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  function handleNext() {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  function hasPrevious() {
    return currentPage > 1
  }

  function hasNext() {
    return currentPage < totalPages
  }

  return (
    <Card className={clsx(fullHeight && 'h-full', 'flex flex-col')}>
      <div className="mb-auto">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">{title}</CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          {isFetching || !topFanList || topFanList.length === 0 ? (
            <EmptyChartData isFetching={isFetching} />
          ) : (
            paginatedTopFanList?.map((fan, i) => {
              return (
                <div
                  key={i}
                  className="flex justify-between gap-2 py-4"
                >
                  <div className="flex min-w-0 max-w-[100px] gap-2 md:min-w-[150px]">
                    {/* TODO: Use real OnlyFans profile pictures. Using this way because API is not working currently */}
                    <Avatar>
                      {fan.avatar && <AvatarImage src={fan.avatar} />}
                      <AvatarFallback className="bg-primary-500 text-xl font-medium text-white">
                        {fan.name
                          .replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '') // Removes Emojis
                          .trim()
                          .at(0) || fan.username.at(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="max-w-[70px] truncate sm:max-w-[150px]">
                      <div className="truncate font-medium text-secondary-500 dark:text-white">
                        <CreatorBrowserLink
                          href={'https://onlyfans.com/' + (fan.username || '')}
                        >
                          <a
                            className="hover:text-primary-500"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {fan.name || fan.username}
                          </a>
                        </CreatorBrowserLink>
                      </div>
                      <div className="truncate text-xs font-medium text-secondary-300">
                        @{fan.username || ''}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-[70px] sm:min-w-[80px]">
                    <div className="font-medium text-secondary-500 dark:text-white">
                      {fan.purchases}
                    </div>
                    <div className="truncate text-xs font-medium text-secondary-300">
                      Purchases
                    </div>
                  </div>
                  <div className="min-w-[70px] sm:min-w-[100px]">
                    <div className="font-medium text-secondary-500 dark:text-white">
                      {getCurrency(fan.totalSpend, showCents ? 2 : 0)}
                    </div>
                    <div className="truncate text-xs font-medium text-secondary-300">
                      Spendings
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </CardContent>
      </div>
      {enablePagination && (
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={!hasPrevious()}
            className="gap-1"
          >
            <IconLeft className="w-2" />
          </Button>
          <div>
            <p className="text-sm font-medium text-secondary-500 dark:text-white">
              Page {currentPage} of {totalPages}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={!hasNext()}
            className="gap-1"
          >
            <IconRight className="w-2" />
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}
